import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import {
  Box,
  darken,
  emphasize,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemText,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { formatISO } from "date-fns";

import constants from "../../assets/constants";
import { getAssessmentPath, getMedicationFormPath } from "../../assets/svgGraphics";
import * as UserSessionActions from "../../state/redux/userSession";
import { deepCopyObject, isValidUUID } from "../../utils/helpers";
import { getAgeFromDateString } from "../../utils/helpers";
import AlertDialog from "../dialogs/alertDialog";
import OmniAvatar from "../ui/omniAvatar";
import Checkbox from "../ui/omniCheckbox";
import PatientAgendaCalendar from "../ui/patientAgendaCalendar";
import Widget from "../ui/widget";
import WidgetAvatar from "../ui/widgetAvatar";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const patientToDoData = [
  {
    key: "toDo0",
    toDoType: "medication",
    name: "GILENYA",
    type: "pill",
    description: "Medication",
    completeBy: formatISO(new Date()),
    isCompleted: false,
    completeDate: null,
  },
  {
    key: "toDo1",
    toDoType: "assessment",
    name: "Medication Adherance",
    type: "Patient Health Questionnaire",
    description: "Patient Health Questionnaire",
    completeBy: formatISO(new Date()),
    isCompleted: false,
    completeDate: null,
  },
];

const PatientAgenda = props => {
  const { patientODM, patientChart } = props;

  const [toDos, setToDos] = useState([...patientToDoData]);
  const [filteredToDos, setFilteredToDos] = useState([...patientToDoData]);
  const [changedToDo, setChangedToDo] = useState(undefined);
  const [showAll, setShowAll] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);

  const getItemIndex = (arr, item) => {
    return arr.findIndex(e => e.key === item);
  };

  useEffect(() => {
    try {
      let result = deepCopyObject(toDos);
      if (!showAll) {
        result = result.filter(({ isCompleted }) => isCompleted === false);
      }
      setFilteredToDos(result);
    } catch (error) {
      console.log(error);
    }
  }, [showAll, toDos, setFilteredToDos]);

  const handleEditButtonClick = toDo => {
    setChangedToDo(toDo);
    if (toDo.isCompleted) {
      setShowAlertDialog(true);
    } else if (toDo.toDoType === "assessment" && !toDo.isCompleted) {
      props.setBpShowChatRequest(true);
    } else {
      toggleIsComplete(toDo.key);
    }
  };

  const toggleIsComplete = key => {
    let newToDos = [...toDos];
    let index = getItemIndex(newToDos, key);
    newToDos[index].isCompleted = !newToDos[index].isCompleted;
    if (newToDos[index].isCompleted) {
      newToDos[index].completeDate = formatISO(new Date());
    } else {
      newToDos[index].completeDate = null;
    }
    setToDos(newToDos);
    setChangedToDo(undefined);
  };

  const handleDialogClose = buttonClicked => {
    setShowAlertDialog(false);
    if (buttonClicked === "ok") {
      toggleIsComplete(changedToDo.key);
    }
    setChangedToDo(undefined);
  };

  const getSvgIconPath = (toDoType, type) => {
    return toDoType === "medication" ? getMedicationFormPath(type) : getAssessmentPath();
  };

  const getEditButtonToolTip = (toDoType, isCompleted) => {
    return toDoType === "medication"
      ? isCompleted
        ? "Mark As Incomplete"
        : "Mark As Complete"
      : "Launch Conversation";
  };

  const getEditButtonIcon = (toDoType, isCompleted) => {
    return isCompleted ? (
      <CheckBoxRoundedIcon sx={{ height: 24, width: 24 }} />
    ) : toDoType === "medication" ? (
      <CheckBoxOutlineBlankRoundedIcon sx={{ height: 24, width: 24 }} />
    ) : (
      <LaunchRoundedIcon sx={{ height: 24, width: 24 }} />
    );
  };

  const getPatientInitials = patientODM => {
    let theReturn =
      patientODM.omniData.givenName.charAt(0) + patientODM.omniData.familyName.charAt(0);

    return theReturn.toUpperCase();
  };

  const loadPatientImage = patientId => {
    try {
      //TODO call DB here to get data retrieval
      let image = `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;

      if (patientId === constants.demoUserId.userTwo) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(1).jpg`;
      }
      if (patientId === constants.demoUserId.userThree) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(22).jpg`;
      }
      if (patientId === constants.demoUserId.userFour) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(11).jpg`;
      }
      return image;
    } catch (error) {
      return `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;
    }
  };

  const [titleText, setTitleText] = useState("");
  const [title, setTitle] = useState("Loading...");

  useEffect(() => {
    if (patientODM != null) {
      setTitleText(
        patientODM.omniData.displayName
          ? patientODM.omniData.displayName
          : `${patientODM.omniData.givenName} ${patientODM.omniData.familyName}`,
      );
      setTitle(
        `${
          patientODM.omniData.patientData.gender
            ? `${patientODM.omniData.patientData.gender}, `
            : " "
        }Age ${getAgeFromDateString(patientODM.omniData.patientData.birthdate)}`,
      );
    } else {
      setTitleText("");
      setTitle("Loading...");
    }
  }, [patientODM]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={patientODM == null}>
        {patientODM && (
          <WidgetAvatar
            src={!isValidUUID(patientODM._id) ? loadPatientImage(patientODM._id) : null}
          >
            {getPatientInitials(patientODM)}
          </WidgetAvatar>
        )}
      </WidgetTitle>
      <Box
        sx={{
          margin: theme.spacing(1),
          marginRight: theme.spacing(2),
          marginLeft: theme.spacing(2),
          paddingBottom: theme.spacing(1),
          marginBottom: `-${theme.spacing(1)}`,
          borderBottom: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
        }}
      >
        <PatientAgendaCalendar />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginRight: theme.spacing(0),
          marginLeft: theme.spacing(2),
          marginTop: `-${theme.spacing(1)}`,
          marginBottom: `-${theme.spacing(2)}`,
        }}
      >
        <Typography variant="overline" sx={{ marginTop: theme.spacing(3) }}>
          To-Do's
        </Typography>
        <Tooltip title={"Show All Tasks"} placement="left">
          <FormControl
            component="fieldset"
            sx={{ marginRight: "14px", marginTop: theme.spacing(2) }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={() => setShowAll(!showAll)}
                  name="showAll"
                  color="secondary"
                />
              }
              labelPlacement="start"
              label={<Typography variant="overline">Show Completed</Typography>}
              sx={{ userSelect: "none", webkitUserSelect: "none", marginTop: "3px" }}
            />
          </FormControl>
        </Tooltip>
      </Box>

      {patientChart == null && <WidgetSkeleton />}
      {patientChart != null && (
        <WidgetList>
          {filteredToDos.map(toDo => {
            return (
              <ListItem
                key={toDo.key}
                disableGutters={true}
                sx={[
                  {
                    paddingRight: theme.spacing(1),
                    paddingLeft: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    boxShadow: theme.shadows[3],
                    cursor: "pointer",
                    userSelect: "none",
                    webkitUserSelect: "none",
                    backgroundColor: alpha(theme.palette.warning.main, 0.75),
                    "&:hover": {
                      backgroundColor: emphasize(theme.palette.warning.main, 0.5),
                      cursor: "pointer",
                    },
                  },
                  toDo.isCompleted && {
                    backgroundColor: alpha(theme.palette.success.main, 0.75),
                    "&:hover": {
                      backgroundColor: emphasize(theme.palette.success.main, 0.5),
                      cursor: "pointer",
                    },
                  },
                ]}
              >
                <Tooltip title={`${toDo.toDoType.toUpperCase()}`} placement="right">
                  <OmniAvatar
                    sx={[
                      {
                        width: 36,
                        height: 36,
                        fontSize: "1.8rem",
                        backgroundColor: theme.palette.info.contrastText,
                        fill: darken(theme.palette.warning.main, 0.5),
                        border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                      },
                      toDo.isCompleted && {
                        color: darken(theme.palette.success.main, 0.5),
                        fill: darken(theme.palette.success.main, 0.5),
                        border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                      },
                    ]}
                  >
                    <SvgIcon
                      sx={[
                        {
                          height: 30,
                          width: 30,
                          color: theme.palette.info.main,
                          backgroundColor: theme.palette.info.contrastText,
                          fill: darken(theme.palette.warning.main, 0.5),
                        },
                        toDo.isCompleted && {
                          fill: darken(theme.palette.success.main, 0.5),
                        },
                      ]}
                    >
                      {getSvgIconPath(toDo.toDoType, toDo.type)}
                    </SvgIcon>
                  </OmniAvatar>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "-2px !Important",
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {toDo.description}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="h6"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        marginLeft: theme.spacing(1),
                        marginTop: `-${theme.spacing(0.75)}`,
                        marginBottom: `-${theme.spacing(0.9)}`,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                        textTransform: "capitalize",
                      }}
                    >
                      {`${toDo.name}`}
                    </Typography>
                  }
                />
                <Tooltip
                  title={getEditButtonToolTip(toDo.toDoType, toDo.isCompleted)}
                  placement="left"
                >
                  <IconButton
                    sx={[
                      {
                        position: "absolute",
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                        height: 32,
                        width: 32,
                        borderRadius: "50%",
                        "&:hover": {
                          color: darken(theme.palette.warning.main, 0.5),
                          background: lighten(theme.palette.warning.main, 0.75),
                        },
                      },
                      toDo.isCompleted && {
                        "&:hover": {
                          color: darken(theme.palette.success.main, 0.5),
                          background: lighten(theme.palette.success.main, 0.75),
                        },
                      },
                    ]}
                    onClick={() => handleEditButtonClick(toDo)}
                    size="large"
                  >
                    {getEditButtonIcon(toDo.toDoType, toDo.isCompleted)}
                  </IconButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </WidgetList>
      )}
      {showAlertDialog && (
        <AlertDialog
          open={showAlertDialog}
          onAccept={() => {
            setShowAlertDialog(false);
            handleDialogClose("ok");
          }}
          onClose={() => {
            setShowAlertDialog(false);
            handleDialogClose("cancel");
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientODM: state.patientSession.patientODM,
  bpSelectedConversationConfiguration: state.userSession.bpSelectedConversationConfiguration,

  //below is hot garbage
  patientImage: state.patientSession.patientImage,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  setBpShowChatRequest: UserSessionActions.setBpShowChatRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientAgenda);
